import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from '@helpcenter/analytics-components';
import { HtmlAnchor } from '@helpcenter/analytics-components/src/html-anchor';
import { fetchUserModelAction } from '@helpcenter/epics-models/src/user';
import { ForgeActionModule } from '@helpcenter/forge-action';
import { USER_MENU_ACTION_SECTION_MODULE } from '@helpcenter/forge-ui-utils/src/constants';
import { ForgeModalContextProvider } from '@helpcenter/forge-ui-utils/src/modal-context-provider';
import { getHelpCenterAri } from '@helpcenter/meta';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useRouter } from 'react-resource-router';

import Badge from '@atlaskit/badge';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { CustomItem, Section } from '@atlaskit/menu';
import { getEnv } from '@atlassian/help-center-common-util/env';

import { fetchOrganisationsModelAction } from '../../epics/model/organisations';
import { fetchUserRequestsCountsAction } from '../../epics/model/user-requests-counts';
import type { State } from '../../state';
import { getCanAdministerJIRA, getCustomMenuLinks } from '../../state/selectors/user';
import { getUserRequestsCounts } from '../../state/selectors/user-requests-counts';
import { parseHelpCenterAri } from '../../utils/ari-parser';
import { JSM_HUB_CUSTOMIZE_MENU_GATE } from '../hub-routes/constants';
import type { CustomMenuLinkModel } from '../../state/persisted/user/reducer';

import { HubConnectMenuSection } from './hub-connect-menu-section';
import { HubForgeMenuSection } from './hub-forge-menu-section';
import messages from './messages';

interface StateProps {
	myRequestCount?: number;
	pendingApprovalCount?: number;
	approvalCount?: number;
	canAdministerJIRA: boolean;
	customMenuLinks: CustomMenuLinkModel[];
}

interface DispatchProps {
	fetchUser: typeof fetchUserModelAction;
	fetchUserRequestsCounts: typeof fetchUserRequestsCountsAction;
	fetchOrganisations: typeof fetchOrganisationsModelAction;
}

type Props = StateProps & DispatchProps;

const HubAvatarMenuOptionsComponent = ({
	fetchUser,
	fetchUserRequestsCounts,
	fetchOrganisations,
	canAdministerJIRA,
	customMenuLinks,
	myRequestCount = 0,
	pendingApprovalCount = 0,
	approvalCount = 0,
}: Props) => {
	di(getEnv, ForgeModalContextProvider, ForgeActionModule);
	const { formatMessage } = useIntl();
	const [{ match }] = useRouter();

	// Fetch user data and request counts on component mount
	useEffect(() => {
		fetchUser();
		fetchOrganisations();
		fetchUserRequestsCounts();
	}, [fetchUser, fetchOrganisations, fetchUserRequestsCounts]);

	// Calculate total pending items for badges
	const totalRequestsCount = myRequestCount || 0;
	const totalApprovalsCount = pendingApprovalCount || 0;
	const { portalId, id: pageId } = match.params;

	const showApprovals = approvalCount > 0 || pendingApprovalCount > 0;
	const helpCenterAri = getHelpCenterAri();
	const helpCenterId = parseHelpCenterAri(helpCenterAri).helpCenterId;

	let editHubSuffix = '';
	if (portalId) {
		editHubSuffix = `/portal/${portalId}`;
	} else if (pageId) {
		editHubSuffix = `/${pageId}`;
	}

	return (
		<ForgeModalContextProvider type={USER_MENU_ACTION_SECTION_MODULE}>
			{!FeatureGates.checkGate('hub_component_upgrade') && (
				<Section hasSeparator>
					<CustomItem
						component={Link}
						actionSubjectId="navbarUserRequests"
						to="/user/requests?reporter=all&statuses=open"
						iconAfter={totalRequestsCount > 0 ? <Badge>{totalRequestsCount}</Badge> : undefined}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className="cv-exclude-from-customisation"
					>
						{formatMessage(messages.requests)}
					</CustomItem>
					{showApprovals && (
						<CustomItem
							component={Link}
							actionSubjectId="navbarUserApprovals"
							to="/user/approvals"
							iconAfter={totalApprovalsCount > 0 ? <Badge>{totalApprovalsCount}</Badge> : undefined}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
							className="cv-exclude-from-customisation"
						>
							{formatMessage(messages.approvals)}
						</CustomItem>
					)}
				</Section>
			)}
			{/* The Customise menu in the nav bar owns these entry points once it ships;
			    keeping both would offer the same two links twice. Read inline rather
			    than through the helper in `hub-routes`, which imports this module. */}
			{canAdministerJIRA && !FeatureGates.checkGate(JSM_HUB_CUSTOMIZE_MENU_GATE) && (
				<Section hasSeparator>
					<CustomItem
						component={HtmlAnchor}
						actionSubjectId="navbarCustomiseHub"
						href={`/helpcenter-management/${helpCenterId}/edit-hub${editHubSuffix}`}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className="cv-exclude-from-customisation"
					>
						{formatMessage(messages.customiseHub)}
					</CustomItem>
					<CustomItem
						component={HtmlAnchor}
						actionSubjectId="navbarHubSettings"
						href={`/helpcenter-management/hubs`}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className="cv-exclude-from-customisation"
					>
						{formatMessage(messages.hubSettings)}
					</CustomItem>
				</Section>
			)}
			<HubConnectMenuSection customMenuLinks={customMenuLinks} />
			<HubForgeMenuSection />
		</ForgeModalContextProvider>
	);
};

const mapStateToProps = (state: State): StateProps => {
	const userRequestsCounts = getUserRequestsCounts(state);
	return {
		myRequestCount: userRequestsCounts.myRequestCount,
		pendingApprovalCount: userRequestsCounts.pendingApprovalCount,
		approvalCount: userRequestsCounts.approvalCount,
		canAdministerJIRA: getCanAdministerJIRA(state),
		customMenuLinks: getCustomMenuLinks(state),
	};
};

export const HubAvatarMenuOptions = connect(mapStateToProps, {
	fetchUser: fetchUserModelAction,
	fetchUserRequestsCounts: fetchUserRequestsCountsAction,
	fetchOrganisations: fetchOrganisationsModelAction,
})(HubAvatarMenuOptionsComponent);
